import React, { useEffect, useState } from "react";

import { DragIcon, PlusThinWhiteIcon, FilterIcon, EditIcon, DeleteRedIcon } from "../../../assets/icons";
import { CategorySelectDto } from "../../../models/categories/categories.response";
import { useNotification } from "../../toster-notification/NotificationContext";
import { categoriesService } from "../../../services/categoriesService";
import { useParams } from "react-router-dom";
import { SubcategorySettingsListItemDTO } from "../../../models/subcategories/subcategories.response";
import { subcategoryService } from "../../../services/subcategoriesService";
import Loader from "../../shared/Loader";
import EditSubcategoryView from "./EditSubcategoryView";
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { confirmDialog } from "../../../utils/confirmDialog";
import AddSubcategoryView from "./AddSubcategoryView";
import SortableSubcategoryRow from "./SortableSubcategoryRow";

const SubcategoryPage: React.FC = () => {

    const [isAddSubcategoryModalOpen, setIsAddSubcategoryModalOpen] = useState(false);
    const [isEditSubcategoryModalOpen, setIsEditSubcategoryModalOpen] = useState(false);

    const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");

    const [subcategories, setSubcategories] = useState<SubcategorySettingsListItemDTO[]>([]);

    const [selectCategories, setSelectCategories] = useState<CategorySelectDto[]>([]);
    const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<string>("");

    const [loading, setLoading] = useState<boolean>(false);

    const { notify } = useNotification();

    const { localId } = useParams<{ localId: string }>();


    useEffect(() => {
        fetchCategoriesSelect();
    }, [localId]);

    const handleDelete = async (id: string) => {

        if (!localId) {
            notify("error", "Local ID nije pronađen.");
            return;
        }

        let confirm = await confirmDialog({
            title: "Potvrda brisanja",
            description: "Da li ste sigurni da želite da obrišete ovu podkategoriju?",
            isDelete: true
        })

        if (!confirm) {
            return;
        }

        setLoading(true);

        try {
            const response = await subcategoryService.deleteSubcategory(localId, id);

            if (response.success) {
                notify("success", "Podkategorija je uspešno obrisana.");
                fetchSubcategories(selectedCategoryId);
            } else {
                notify("error", "Došlo je do greške prilikom brisanja podkategorije.");
            }
        } catch (error) {
            notify("error", "Došlo je do greške prilikom brisanja podkategorije.");
        } finally {
            setLoading(false);
        }
    };

    const fetchSubcategories = async (categoryId: string) => {
        if (!localId) {
            notify("error", "Local ID nije pronađen.");
            return;
        }

        if (categoryId.trim() === "") {
            return;
        }

        setLoading(true);
        try {
            const response = await subcategoryService.getSubcategoriesByCategoryId(localId, categoryId);

            if (response.success && response.data) {
                setSubcategories(response.data);
            } else {
                notify("error", "Došlo je do greške prilikom učitavanja kategorija.");
            }

        } catch (error) {
            notify("error", "Došlo je do greške prilikom učitavanja kategorija.");
        } finally {
            setLoading(false);
        }
    };

    const fetchCategoriesSelect = async () => {

        if (localId === undefined) {
            notify("error", "Local ID is missing.");
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const response = await categoriesService.getCategoriesSelect(localId);

            if (response.success && response.data) {
                setSelectCategories(response.data);
                if (response.data.length > 0) {
                    const defaultCategoryId = response.data[0].id;
                    setSelectedCategoryId(defaultCategoryId);
                    fetchSubcategories(defaultCategoryId);
                }
            } else {
                notify("error", "Došlo je do greške prilikom učitavanja kategorija.");
            }

        } catch (error) {
            notify("error", "Došlo je do greške prilikom učitavanja kategorija.");
        } finally {
            setLoading(false);
        }
    };

    const persistOrder = async (updated: SubcategorySettingsListItemDTO[]) => {
        if (localId === undefined || localId === null) {
            notify("error", "Local ID nije pronađen.");
            return;
        }

        setLoading(true);
        try {
            const orderRecord: Record<string, number> = {};
            updated.forEach((c) => {
                orderRecord[c.id] = c.order;
            });

            const response = await subcategoryService.sortSubcategories(localId, { subcategories: orderRecord });

            if (response.success) {
                notify("success", "Redosled podkategorija uspešno sačuvan.");
            } else {
                notify("error", "Greška pri čuvanju redosleda podkategorija.");
            }

        } catch (error: any) {
            if (error.response && error.response.data && error.response.data.message) {
                notify("error", error.response.data.message);
            } else {
                notify("error", "Greška pri čuvanju redosleda podkategorija.");
            }
        } finally {
            setLoading(false);
        }
    };

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over) return;
        if (active.id === over.id) return;

        setSubcategories((prev) => {
            const oldIndex = prev.findIndex((c) => c.id === active.id);
            const newIndex = prev.findIndex((c) => c.id === over.id);

            const moved = arrayMove(prev, oldIndex, newIndex);

            const updated = moved.map((c, idx) => ({
                ...c,
                order: idx + 1,
            }));

            // 3) pozovi snimanje (side-effect van setState-a)
            queueMicrotask(() => persistOrder(updated));

            return updated;
        });
    };

    if (loading) {
        return <Loader />;
    }

    if (localId === undefined) {
        return <div>Local ID is missing.</div>;
    }

    return (
        <>
            {isAddSubcategoryModalOpen && (
                <AddSubcategoryView
                    localId={localId}
                    onClose={() => {
                        setIsAddSubcategoryModalOpen(false);
                        fetchSubcategories(selectedCategoryId);
                    }}
                />
            )}

            {isEditSubcategoryModalOpen && (
                <EditSubcategoryView
                    subcategoryId={selectedSubcategoryId}
                    localId={localId}
                    onClose={() => {
                        setIsEditSubcategoryModalOpen(false);
                        fetchSubcategories(selectedCategoryId);
                    }}
                />
            )}

            <section className="subcategory-section">
                <div className="w-layout-blockcontainer container subcategory w-container">
                    <div className="sttings-content-wrapper">
                        <div className="header-div">
                            <div>
                                <h2 className="heading-3">Podkategorije</h2>
                                <div className="description-text">
                                    Upravljajte podkategorijama unutar kategorija<br />
                                </div>
                            </div>
                            <div className="header-button-div">
                                <div className="button-primary " onClick={() => setIsAddSubcategoryModalOpen(true)}>
                                    <img loading="lazy" src={PlusThinWhiteIcon} alt="" className="button-image" />
                                    <div className="button-text">Nova podkategorija</div>
                                </div>
                            </div>
                        </div>
                        <div className="admin-settings-box-div table-block-div">
                            <div className="list-header">
                                <div className="list-header-info">
                                    <div className="list-header-title">Sve podkategorije</div>
                                    <div className="list-header-description">
                                        Ukupno podkategorija: {subcategories.length} ({subcategories.filter(s => !s.isDeleted).length} aktivnih)<br />
                                    </div>
                                </div>
                                <div className="list-header-filer">
                                    <img loading="lazy" src={FilterIcon} alt="" className="subcategory-header-filter-image" />
                                    <div className="list-header-filer-form w-form">
                                        <form method="get">
                                            <select className="form-select lm-auto w-select"
                                                value={selectedCategoryId}
                                                onChange={(e) => {
                                                    setSelectedCategoryId(e.target.value);
                                                    fetchSubcategories(e.target.value);
                                                }}
                                            >

                                                {selectCategories.length > 0 ? (
                                                    selectCategories.map((category) => (
                                                        <option key={category.id} value={category.id}>{category.name}</option>
                                                    ))
                                                ) : (
                                                    <option value="">Nema dostupnih kategorija</option>
                                                )}

                                            </select>
                                        </form>
                                    </div>
                                </div>
                            </div>
                            <div className="list-div subcategory">
                                <div className="list-row-div list-header-row subcategory-row">
                                    <div className="div-block"></div>
                                    <div>Naziv</div>
                                    <div>Kategorija</div>
                                    <div>Slug</div>
                                    <div>Status</div>
                                    <div id="w-node-_784b75b9-7580-94fe-59b3-83eda5076a1f-1cee91cf">Akcije</div>
                                </div>

                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDragEnd}
                                >
                                    <SortableContext
                                        items={subcategories.map((c) => c.id)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        {subcategories
                                            .slice()
                                            .sort((a, b) => a.order - b.order)
                                            .map((subcategory) => (
                                                <SortableSubcategoryRow
                                                    key={subcategory.id}
                                                    subcategory={subcategory}
                                                    onEdit={(id) => {
                                                        setSelectedSubcategoryId(id);
                                                        setIsEditSubcategoryModalOpen(true);
                                                    }}
                                                    onDelete={(id) => handleDelete(id)}
                                                />
                                            ))}
                                    </SortableContext>
                                </DndContext>

                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default SubcategoryPage;